import { reactive } from 'vue'
import { Capacitor } from '@capacitor/core'
import Crashlytics from '@/services/Crashlytics'
import ConfigService from '@/services/ConfigService'
import AppService from '@/services/AppService'
import Analytics from '@/services/Analytics'
import { Purchases } from "@revenuecat/purchases-capacitor";
import { Network } from '@capacitor/network'
import mockSubscriptionData from '@/services/mockSubscriptionData.json'
import { cloneDeep } from 'lodash'

const LIFETIME_PRODUCTS = [ 'pro_life', 'pro_lifetime' ]

// TODO rename to PACKAGE_PRO and PACKAGE_FREE
export const ACCESS_FREE = 1
export const ACCESS_PRO = 2
export const ENTITLEMENT_PRO = 'pro'

let plan = import.meta.env.MODE === 'development' ? ACCESS_FREE : ACCESS_FREE

const state = reactive({
  plan,
  product: null,
  offering: 'triple' // FIXME DEFAULT_CONFIG is asynchronously loaded so not available here
})

// Mock products for web development (loaded from JSON)
const MOCK_PRODUCTS = mockSubscriptionData.products

// Mock packages for web development (monthly, yearly, lifetime)
const MOCK_PACKAGES = mockSubscriptionData.packages.map(pkg => ({
  ...pkg,
  product: MOCK_PRODUCTS[pkg.productKey]
}))

class PurchasesError extends Error {
  constructor (purchaseError) {
    super(purchaseError.message)

    this.code = purchaseError.code
    this.readableErrorCode = purchaseError.readableErrorCode
    if (purchaseError.underlyingErrorMessage) {
      this.underlyingErrorMessage = purchaseError.underlyingErrorMessage
    }
  }
}

/**
 * Update current plan based on customer info
 * @param customerInfo
 * @returns {void}
 */
function updatePlan (customerInfo) {
  const currentEntitlements = Object.keys(customerInfo.entitlements?.active || {})

  state.plan = currentEntitlements.includes(ENTITLEMENT_PRO)
    ? ACCESS_PRO
    : ACCESS_FREE

  // Store current product identifier. Required for detection of lifetime plan.
  if (customerInfo.entitlements.active[ENTITLEMENT_PRO]) {
    state.product = customerInfo.entitlements.active[ENTITLEMENT_PRO].productIdentifier
  }
}


export default {
  state,

  getAccessiblePackages (packageId) {
    const accessible = [ACCESS_FREE]
    const pid = packageId ?? state.plan

    if (pid !== ACCESS_FREE) {
      accessible.push(pid)
    }

    return accessible
  },

  /**
   *
   * @param {Object} content
   * @param {Number} [packageId]
   * @returns {boolean}
   */
  hasAccess (content, packageId) {
    const p = packageId ?? state.plan

    // Unmarked or free content is always accessible,
    // alternatively it must match current entitlement
    return typeof content.package_id === 'undefined' ||
      content.package_id === null ||
      content.package_id === ACCESS_FREE ||
      content.package_id === p
  },

  isPro () {
    return state.plan === ACCESS_PRO
  },

  /**
   * Initialize subscriptions service for the user
   *
   * @param id
   * @param email
   * @param displayName
   * @returns {Promise<void>}
   */
  async init ({ id: appUserID, email, displayName }) {
    // Skip for not native platforms
    if (!Capacitor.isNativePlatform()) return

    // Skip if configured
    const { isConfigured } = await Purchases.isConfigured()
    if (isConfigured) return

    // window.Purchases.setDebugLogsEnabled(true)

    state.offering = await ConfigService.getString('offering')
    // console.log('SubService: offering', state.offering)

    let apiKey

    if (Capacitor.getPlatform() === 'android') {
      apiKey = await ConfigService.getString('rc_public_google_key')
    } else if (Capacitor.getPlatform() === 'ios') {
      apiKey = await ConfigService.getString('rc_public_apple_key')
    }

    // Configure RevenueCat
    await Purchases.configure({
      apiKey,
      appUserID
    })

    // Get firebase app instance ID
    const firebaseAppInstanceID = await Analytics.getAppInstanceId().catch(() => null)

    // Collect device identifiers, set app instance ID and user attributes
    await Promise.all([
      Purchases.collectDeviceIdentifiers(),
      Purchases.setAttributes({ 'forexHeroAppInstanceId': AppService.getAppId() }),
      appUserID && this.setUserAttributes({ email, displayName }),
      firebaseAppInstanceID && Purchases.setFirebaseAppInstanceID({ firebaseAppInstanceID }),
      Purchases.addCustomerInfoUpdateListener(updatePlan)
    ])
  },

  async setAppsFlyerId (appsflyerID) {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured || !appsflyerID) return

    // const { isAnonymous } = await Purchases.isAnonymous()

    return Purchases.setAppsflyerID({ appsflyerID })
  },

  /**
   * Promise wrapper for getAppUserID
   * @returns {Promise<String|null>}
   */
  async getRevenueCatUserId () {
    if (!Capacitor.isNativePlatform()) return null

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return null

    const { appUserID } = await Purchases.getAppUserID()
    return appUserID
  },

  async logIn ({ id, email, displayName }) {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    try {
      const { customerInfo } = await Purchases.logIn({ appUserID: id })
      await this.setUserAttributes({ email, displayName })
      return customerInfo
    } catch (error) {
      const e = new PurchasesError(error)
      console.warn('RevenueCat login failed', e)
      Crashlytics.recordExceptionWithStacktrace('RevenueCat login failed', e)
      throw e
    }
  },

  async setUserAttributes ({ email, displayName }) {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    return Promise.all([
      email && Purchases.setEmail(email),
      displayName && Purchases.setDisplayName(displayName)
    ])
  },

  async logOut () {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    try {
      await Purchases.logOut()
      state.plan = ACCESS_FREE
    } catch (error) {
      const e = new PurchasesError(error)
      console.warn('RevenueCat logout failed', e)
      Crashlytics.recordExceptionWithStacktrace('RevenueCat logout failed', e)
      throw e
    }
  },

  async getCustomerInfo () {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    return Purchases.getCustomerInfo()
      .then(({ customerInfo }) => customerInfo)
      .catch(error => {
        const e = new PurchasesError(error)
        console.warn('Error fetching customer info', e)
        Crashlytics.recordExceptionWithStacktrace('Error fetching customer info', e)
        throw e
      })
  },

  async refreshCustomerInfo () {
    if (!Capacitor.isNativePlatform()) return

    // Skip refresh if not connected
    const { connected } = await Network.getStatus()
    if (!connected) return

    const info = await this.getCustomerInfo()
    updatePlan(info)
  },

  async getAvailablePackages () {
    // Return mock packages for web development
    if (!Capacitor.isNativePlatform()) {
      return MOCK_PACKAGES
    }

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return []

    try {
      const offerings = await Purchases.getOfferings()

      if (offerings.current !== null && offerings.current.availablePackages.length !== 0) {
        return offerings.current.availablePackages
      } else {
        const message = `Offering ${state.offering} not found`
        console.warn(message)
        throw new Error(message)
      }
    } catch (e) {
      console.warn('Failed getting RevenueCat offerings', e)
      Crashlytics.recordExceptionWithStacktrace('Failed getting RevenueCat offerings', e)
      throw e
    }
  },

  /**
   * Get products by their identifiers
   * Wraps Purchases.getProducts with web mockup support
   *
   * @param {string[]} productIdentifiers - Array of product identifiers to fetch
   * @returns {Promise<{ products: Object[] }>}
   */
  async getProducts (productIdentifiers) {
    // Return mock products for web development
    if (!Capacitor.isNativePlatform()) {
      const products = productIdentifiers
        .map(id => MOCK_PRODUCTS[id])
        .filter(Boolean)
      return { products }
    }

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return { products: [] }

    try {
      return await Purchases.getProducts({ productIdentifiers })
    } catch (e) {
      console.warn('Failed getting RevenueCat products', e)
      Crashlytics.recordExceptionWithStacktrace('Failed getting RevenueCat products', e)
      throw e
    }
  },

  async purchaseProduct (product) {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    return Purchases.purchaseStoreProduct({ product: cloneDeep(product) })
      .then(({ customerInfo }) => updatePlan(customerInfo))
      .catch((error) => {
        // Error making purchase
        const e = new PurchasesError(error.message)
        console.warn('Error making purchase', e)
        Crashlytics.recordExceptionWithStacktrace(e.message, e)
        throw e
      })
  },

  async purchase (productId) {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    const packs = await this.getAvailablePackages()

    const pack = packs.find(p => p.product.identifier === productId)

    if (!pack) {
      const message = `Package ${productId} not found`
      throw new Error(message)
    }

    return Purchases.purchasePackage({ aPackage: pack })
      .then(({ productIdentifier, customerInfo }) => updatePlan(customerInfo))
      .catch((error) => {
        // Error making purchase
        const e = new PurchasesError(error.message)
        console.warn('Error making purchase', e)
        Crashlytics.recordExceptionWithStacktrace(e.message, e)
        throw e
      })
  },

  /**
   * Restores purchases for the user.
   *
   * @returns {Promise<Object>} A Promise that resolves when the restore process is complete.
   * @throws {PurchasesError} If there was an error restoring purchases.
   */
  async restore () {
    if (!Capacitor.isNativePlatform()) return

    const { isConfigured } = await Purchases.isConfigured()
    if (!isConfigured) return

    try {
      const { customerInfo } = await Purchases.restorePurchases()
      updatePlan(customerInfo)
      return customerInfo
    } catch (error) {
      const e = new PurchasesError(error)
      console.warn('Error restoring purchases', e)
      Crashlytics.recordExceptionWithStacktrace('Error restoring purchases', e)
      throw e
    }
  },

  async getManagementUrl () {
    const { managementURL } = await this.getCustomerInfo()
    return managementURL
  },

  isLifetime () {
    return state.product &&
      LIFETIME_PRODUCTS.some(key => state.product.startsWith(key))
  }
}
